/**
 * Reading the parts of a field's JSON schema the custom fields of the
 * `odoo-project` form rely on. The schema is the template's: each field takes
 * its options, bounds and labels from it rather than restating them, so that
 * the template stays the one place they are decided.
 */

/** A JSON schema as RJSF hands it to a field: an open object. */
export type SchemaObject = { [key: string]: unknown };

export function isSchemaObject(value: unknown): value is SchemaObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** `schema.properties[key]`, when it is a schema object. */
export function propertySchema(
  schema: SchemaObject,
  key: string,
): SchemaObject | undefined {
  const properties = schema.properties;
  if (!isSchemaObject(properties)) {
    return undefined;
  }
  const property = properties[key];
  return isSchemaObject(property) ? property : undefined;
}

/** The keys of `schema.properties`, in the template's order. */
export function propertyKeys(schema: SchemaObject): string[] {
  return isSchemaObject(schema.properties)
    ? Object.keys(schema.properties)
    : [];
}

export interface EnumOption<T> {
  value: T;
  label: string;
}

/**
 * The options of an `enum`, labelled by `enumNames` (the scaffolder's
 * non-standard but long-standing keyword) where it has an entry.
 */
export function enumOptions<T extends string | number>(
  schema: SchemaObject | undefined,
  isValue: (value: unknown) => value is T,
): EnumOption<T>[] {
  if (!schema || !Array.isArray(schema.enum)) {
    return [];
  }
  const names = Array.isArray(schema.enumNames) ? schema.enumNames : [];
  return schema.enum.filter(isValue).map(value => {
    const name = names[(schema.enum as unknown[]).indexOf(value)];
    return { value, label: typeof name === 'string' ? name : String(value) };
  });
}

export const isString = (value: unknown): value is string =>
  typeof value === 'string';

export const isNumber = (value: unknown): value is number =>
  typeof value === 'number';

export function numberKeyword(
  schema: SchemaObject | undefined,
  keyword: 'minimum' | 'maximum' | 'multipleOf',
): number | undefined {
  const value = schema?.[keyword];
  return typeof value === 'number' ? value : undefined;
}

export function stringKeyword(
  schema: SchemaObject | undefined,
  keyword: 'title' | 'description' | 'pattern' | 'type',
): string | undefined {
  const value = schema?.[keyword];
  return typeof value === 'string' ? value : undefined;
}

/**
 * The messages of an RJSF error schema at `path` (`__errors` of the nested
 * object), for a field that renders its own nested inputs.
 */
export function errorsAt(errorSchema: unknown, path: string[]): string[] {
  let node: unknown = errorSchema;
  for (const key of path) {
    if (!isSchemaObject(node)) {
      return [];
    }
    node = node[key];
  }
  if (!isSchemaObject(node) || !Array.isArray(node.__errors)) {
    return [];
  }
  return node.__errors.filter(isString);
}

/**
 * The whole form's data, as the scaffolder's stepper passes it to every field
 * in `formContext.formData` (all the steps, not only the current one —
 * plugin-scaffolder-react `Stepper`). `{}` when absent.
 */
export function formDataOf(formContext: unknown): SchemaObject {
  if (isSchemaObject(formContext) && isSchemaObject(formContext.formData)) {
    return formContext.formData;
  }
  return {};
}

/** A string of the whole form's data, or undefined. */
export function formString(
  formContext: unknown,
  key: string,
): string | undefined {
  const value = formDataOf(formContext)[key];
  return isString(value) ? value : undefined;
}
